import { flattenCollection } from '@features/opportunities/services/contract';

export namespace Product {
  export interface Term {
    rate: number;
    term: number;
    rateType: string;
    repaymentType: string;
  }

  export interface MultiTerm {
    initialTerm: Term;
    revertTerm: Term;
  }

  export interface ProductStructure {
    singleTerm: Term | null;
    multiTerm: MultiTerm | null;
  }

  export interface Split {
    id: string;
    providerId: number | null;
    providerName: string;
    productId: number | null;
    productName: string;
    splitAmount: number;
    frequency: string | null;
    repaymentAmount: number | null;
    structure: ProductStructure | null;
  }

  export interface Summary {
    opportunityId: string;
    isInsuranceProduct: boolean;
    settlementDate: string;
    settlementAmount: number;
    accountName: string;
    accountReference: string | null;
    splits: Split[];
  }

  export interface SplitContract {
    id: string;
    providerId: number;
    providerName: string;
    productId: number;
    productName: string;
    splitAmount: number;
    frequency: string;
    repaymentAmount: number;
    structure: ProductStructure;
  }

  export interface RefixSplitContract {
    id: string;
    providerName: string;
    productName: string;
    splitAmount: number;
    frequency: string;
    repaymentAmount: number;
    structure: ProductStructure;
  }

  export interface InsuranceSplitContract {
    id: string;
    providerName: string;
    productName: string;
    splitAmount: number;
    frequency: string;
    repaymentAmount: number;
  }

  export interface CombinedSplitContract {
    common?: SplitContract[];
    refix?: RefixSplitContract[];
    insurance?: InsuranceSplitContract[];
  }

  export interface SummaryContract {
    opportunityId: string;
    settlementDate: string;
    settlementAmount: number;
    accountName: string;
    accountReference: string | null;
    splitsV2?: CombinedSplitContract;
  }

  export function toInsuranceSplitModel(split: InsuranceSplitContract): Split {
    return {
      id: split.id,
      providerId: null,
      providerName: split.providerName,
      productId: null,
      productName: split.productName,
      splitAmount: split.splitAmount,
      frequency: split.frequency,
      repaymentAmount: split.repaymentAmount,
      structure: null
    };
  }

  export function toRefixSplitModel(split: RefixSplitContract): Split {
    return {
      id: split.id,
      providerId: null,
      providerName: split.providerName,
      productId: null,
      productName: split.productName,
      splitAmount: split.splitAmount,
      frequency: split.frequency,
      repaymentAmount: split.repaymentAmount,
      structure: split.structure
    };
  }

  export function toSplitModel(split: SplitContract): Split {
    return {
      id: split.id,
      providerId: split.providerId,
      providerName: split.providerName,
      productId: split.productId,
      productName: split.productName,
      splitAmount: split.splitAmount,
      frequency: split.frequency,
      repaymentAmount: split.repaymentAmount,
      structure: split.structure
    };
  }

  export function sortBySettlementDate(summaries: Summary[]): Summary[] {
    return summaries.sort((a, b) => {
      let daysA = a.settlementDate ? new Date(a.settlementDate) : new Date(0);
      let daysB = b.settlementDate ? new Date(b.settlementDate) : new Date(0);
      return daysB.getTime() - daysA.getTime();
    });
  }

  export function toSummaryModel(products: SummaryContract[]): Summary[] {
    const filteredProducts = products.filter(
      (x) =>
        (x.splitsV2?.common && x.splitsV2.common.length > 0) ||
        (x.splitsV2?.insurance && x.splitsV2?.insurance.length > 0) ||
        (x.splitsV2?.refix && x.splitsV2?.refix.length > 0)
    );
    const result = flattenCollection<SummaryContract>(
      filteredProducts
    ).map<Summary>((x) => {
      const insuranceSplits = flattenCollection<InsuranceSplitContract>(
        x.splitsV2?.insurance || []
      ).map(toInsuranceSplitModel);
      const refixSplits = flattenCollection<RefixSplitContract>(
        x.splitsV2?.refix || []
      ).map(toRefixSplitModel);
      const commonSplits = flattenCollection<SplitContract>(
        x.splitsV2?.common || []
      ).map(toSplitModel);
      return {
        opportunityId: x.opportunityId,
        isInsuranceProduct: insuranceSplits.length > 0,
        settlementDate: x.settlementDate,
        settlementAmount: x.settlementAmount,
        accountName: x.accountName,
        accountReference: x.accountReference,
        splits: [...insuranceSplits, ...refixSplits, ...commonSplits]
      };
    });

    return sortBySettlementDate(result);
  }
}
